import { focusHaloFlush, panelMotion } from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import { orpc } from "@/lib/api";
import { formatTimeAgo } from "@/lib/format";

/** Text that eases to its new width when it changes, so a swap reads as one thing turning over. */
function MorphText({ view, children }: { view: string; children: ReactNode }) {
  const [width, setWidth] = useState<number>();
  const measure = useCallback((pane: HTMLSpanElement | null) => {
    if (!pane) return;
    const read = () => setWidth(Math.ceil(pane.getBoundingClientRect().width));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(pane);
    return () => observer.disconnect();
  }, []);
  return (
    <span
      className="inline-block max-w-full overflow-hidden align-bottom whitespace-nowrap transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ width }}
    >
      <span
        key={view}
        ref={measure}
        className="inline-block animate-in fade-in duration-200 ease-out"
      >
        {children}
      </span>
    </span>
  );
}

const face = `rounded-md px-0.5 -mx-0.5 hover:bg-foreground/5 ${focusHaloFlush} ${panelMotion}`;

/**
 * A moment as a clock reads it, which turns into how long ago it was when pressed, and back.
 * `precise` adds milliseconds, for telling apart things a moment apart.
 */
export function Timestamp({
  at,
  precise = false,
  dateless = false,
  startRelative = false,
}: {
  at: string | number;
  precise?: boolean;
  /** Leave the date off, where it is plain from around it. */
  dateless?: boolean;
  /** Show how long ago first, and the clock when pressed. */
  startRelative?: boolean;
}) {
  const [relative, setRelative] = useState(startRelative);
  const date = new Date(at);
  const clock = dateless
    ? date.toLocaleTimeString()
    : date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
      });
  const exact = precise ? `${clock}.${String(date.getMilliseconds()).padStart(3, "0")}` : clock;
  return (
    <button
      type="button"
      title={relative ? exact : "Show how long ago"}
      onClick={() => setRelative(!relative)}
      className={`inline-block max-w-full align-bottom tabular-nums ${face}`}
    >
      <MorphText view={relative ? "relative" : "exact"}>
        {relative ? formatTimeAgo(date.toISOString()) : exact}
      </MorphText>
    </button>
  );
}

/**
 * A user id, which turns into the person's display name when pressed, with a link that opens their
 * profile in a new tab. Pressing the name turns it back. A `guest` has no profile, so is named
 * "Guest" without asking; an id not known to be one that turns out to have no profile is named the
 * same way.
 */
export function UserId({ id, guest = false }: { id: string; guest?: boolean }) {
  const [named, setNamed] = useState(false);
  const profile = useQuery({
    ...orpc.profiles.get.queryOptions({
      input: { params: { profileId: id } },
      select: response => response.profile,
    }),
    enabled: named && !guest,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const name = guest || profile.isError ? "Guest" : profile.data ? profile.data.displayName : "…";
  return (
    <span className="inline-flex max-w-full items-baseline gap-0.5">
      <button
        type="button"
        title={named ? id : "Show who this is"}
        onClick={() => setNamed(!named)}
        className={`min-w-0 ${named ? "" : "font-mono"} ${face}`}
      >
        <MorphText view={named ? name : "id"}>{named ? name : id}</MorphText>
      </button>
      {named && name !== "Guest" && (
        <a
          href={`/profiles/${id}`}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${name === "…" ? "the" : `${name}'s`} profile in a new tab`}
          title="Open the profile in a new tab"
          className={`self-center text-muted-foreground hover:text-foreground ${face}`}
        >
          <ExternalLink className="size-3" />
        </a>
      )}
    </span>
  );
}
